/**
 * Geofence Calculations
 *
 * Handles distance calculations and geofence status checks.
 */

import type { GeoLocation, GeofenceStatus } from '@/lib/types'
import {
  LORA_EXTENDED_BOUNDARY_METERS,
  LORA_EXTENDED_BOUNDARY_ACTIVATION_THRESHOLD,
} from '@/lib/types'

// Earth radius in meters
const EARTH_RADIUS = 6371000

/**
 * Calculate distance between two points using Haversine formula
 */
export function calculateDistance(
  point1: GeoLocation,
  point2: GeoLocation
): number {
  const lat1Rad = toRadians(point1.latitude)
  const lat2Rad = toRadians(point2.latitude)
  const deltaLat = toRadians(point2.latitude - point1.latitude)
  const deltaLng = toRadians(point2.longitude - point1.longitude)

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1Rad) *
      Math.cos(lat2Rad) *
      Math.sin(deltaLng / 2) *
      Math.sin(deltaLng / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return EARTH_RADIUS * c // Distance in meters
}

/**
 * Check if a point is inside a circular geofence
 */
export function isInsideGeofence(
  point: GeoLocation,
  center: GeoLocation,
  radiusMeters: number
): boolean {
  const distance = calculateDistance(point, center)
  return distance <= radiusMeters
}

/**
 * Get detailed geofence status including distances
 */
export function getGeofenceStatus(
  point: GeoLocation,
  center: GeoLocation,
  radiusMeters: number
): GeofenceStatus {
  const distanceToCenter = calculateDistance(point, center)
  const isInside = distanceToCenter <= radiusMeters

  return {
    isInside,
    distanceToCenter,
    distanceBeyondRadius: isInside ? undefined : distanceToCenter - radiusMeters,
    effectiveRadius: radiusMeters,
  }
}

/**
 * Options for transport-aware geofence checking
 */
export interface TransportAwareGeofenceOptions {
  /** The user's current location */
  point: GeoLocation
  /** The room center */
  center: GeoLocation
  /** The room's configured radius */
  radiusMeters: number
  /** Whether LoRa is connected */
  isLoRaConnected?: boolean
}

/**
 * Get geofence status with transport-aware boundary extension.
 *
 * When a user is connected via LoRa, we extend the acceptable boundary
 * to 10km since LoRa mesh can reach 2-15km. This allows users who are
 * physically outside the standard room geofence (max 500m) to still
 * participate if they're connected via LoRa mesh.
 *
 * This is important because:
 * 1. LoRa devices may be positioned at elevated locations for range
 * 2. Mesh nodes may be spread across a wider area than the room boundary
 * 3. In emergencies, accepting distant connections is better than isolation
 */
export function getTransportAwareGeofenceStatus(
  options: TransportAwareGeofenceOptions
): GeofenceStatus {
  const { point, center, radiusMeters, isLoRaConnected } = options

  const distanceToCenter = calculateDistance(point, center)

  // Determine if we should use LoRa extended boundary
  const shouldExtend = isLoRaConnected &&
    radiusMeters >= LORA_EXTENDED_BOUNDARY_ACTIVATION_THRESHOLD

  // Use extended boundary for LoRa connections
  const effectiveRadius = shouldExtend
    ? Math.max(radiusMeters, LORA_EXTENDED_BOUNDARY_METERS)
    : radiusMeters

  const isInside = distanceToCenter <= effectiveRadius

  // For LoRa extended boundary, also track if within original boundary
  const isInsideOriginal = distanceToCenter <= radiusMeters

  return {
    isInside,
    distanceToCenter,
    distanceBeyondRadius: isInside ? undefined : distanceToCenter - effectiveRadius,
    isLoRaExtended: shouldExtend && !isInsideOriginal && isInside,
    effectiveRadius,
  }
}

/**
 * Format distance for display
 */
export function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${Math.round(meters)}m`
  }
  return `${(meters / 1000).toFixed(1)}km`
}

/**
 * Get bearing between two points (in degrees)
 */
export function calculateBearing(
  from: GeoLocation,
  to: GeoLocation
): number {
  const lat1Rad = toRadians(from.latitude)
  const lat2Rad = toRadians(to.latitude)
  const deltaLng = toRadians(to.longitude - from.longitude)

  const y = Math.sin(deltaLng) * Math.cos(lat2Rad)
  const x =
    Math.cos(lat1Rad) * Math.sin(lat2Rad) -
    Math.sin(lat1Rad) * Math.cos(lat2Rad) * Math.cos(deltaLng)

  const bearing = toDegrees(Math.atan2(y, x))
  return (bearing + 360) % 360 // Normalize to 0-360
}

/**
 * Get compass direction from bearing
 */
export function bearingToDirection(bearing: number): string {
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW']
  const index = Math.round(bearing / 45) % 8
  return directions[index]!
}

// ============= Utility Functions =============

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180
}

function toDegrees(radians: number): number {
  return (radians * 180) / Math.PI
}
